import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import LoadingState from "../../../components/LoadingState/index.js";
import EmptyState from "../../../components/EmptyState/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { useQuery } from "../../../hooks/useApi.js";
import {
  developerUsersService,
  type UserWithUnverifiedFiles,
  type NormalizeSubmissionsResponse,
} from "../../../api/developerUsers.service.js";
import styles from "./Users.module.css";
import { StatusBadge, isValidFirmRef, type NormalizeStatus } from "./shared.js";

type UserRow = UserWithUnverifiedFiles & {
  normalizeStatus: NormalizeStatus;
  result?: NormalizeSubmissionsResponse;
  error?: string;
};

function UnverifiedFilesTab() {
  const { data, loading, error, refetch } = useQuery(
    () => developerUsersService.getUsersWithUnverifiedFiles(),
    [],
  );

  const [userRows, setUserRows] = useState<UserRow[]>([]);
  const [normalizeAllLoading, setNormalizeAllLoading] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    if (data?.users) {
      setUserRows(
        data.users.map(user => ({
          ...user,
          normalizeStatus: "pending" as NormalizeStatus,
        })),
      );
    }
  }, [data]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleNormalize = useCallback(async (user: UserRow) => {
    if (user.firms.length > 1) {
      setUserRows(prev =>
        prev.map(u =>
          u.externalId === user.externalId
            ? {
                ...u,
                normalizeStatus: "failed" as NormalizeStatus,
                error: `User has ${user.firms.length} firms — manual normalization required`,
              }
            : u,
        ),
      );
      return;
    }

    const firm = user.firms[0];
    if (!firm || !isValidFirmRef(firm.firmRef)) return;

    setUserRows(prev =>
      prev.map(u =>
        u.externalId === user.externalId
          ? { ...u, normalizeStatus: "loading" as NormalizeStatus }
          : u,
      ),
    );

    try {
      const result = await developerUsersService.normalizeSubmissions({
        userId: user.externalId,
        firmRef: firm.firmRef,
        firmName: firm.firmName,
        force: true,
      });

      setUserRows(prev =>
        prev.map(u =>
          u.externalId === user.externalId
            ? { ...u, normalizeStatus: "success" as NormalizeStatus, result }
            : u,
        ),
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to normalize";
      setUserRows(prev =>
        prev.map(u =>
          u.externalId === user.externalId
            ? { ...u, normalizeStatus: "failed" as NormalizeStatus, error: errorMessage }
            : u,
        ),
      );
    }
  }, []);

  const handleNormalizeAll = useCallback(async () => {
    const pendingUsers = userRows.filter(
      u =>
        u.normalizeStatus === "pending" &&
        u.firms.length === 1 &&
        isValidFirmRef(u.firms[0]?.firmRef),
    );
    if (pendingUsers.length === 0) return;

    if (!window.confirm(`Normalize submissions for ${pendingUsers.length} user(s)?`)) return;

    setNormalizeAllLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const user of pendingUsers) {
      const firm = user.firms[0];
      if (!firm) continue;
      try {
        setUserRows(prev =>
          prev.map(u =>
            u.externalId === user.externalId
              ? { ...u, normalizeStatus: "loading" as NormalizeStatus }
              : u,
          ),
        );

        const result = await developerUsersService.normalizeSubmissions({
          userId: user.externalId,
          firmRef: firm.firmRef,
          firmName: firm.firmName,
          force: true,
        });

        setUserRows(prev =>
          prev.map(u =>
            u.externalId === user.externalId
              ? { ...u, normalizeStatus: "success" as NormalizeStatus, result }
              : u,
          ),
        );
        successCount++;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to normalize";
        setUserRows(prev =>
          prev.map(u =>
            u.externalId === user.externalId
              ? { ...u, normalizeStatus: "failed" as NormalizeStatus, error: errorMessage }
              : u,
          ),
        );
        failCount++;
      }
    }

    setNormalizeAllLoading(false);
    setNotification({
      type: failCount === 0 ? "success" : "error",
      message:
        failCount === 0
          ? `Successfully normalized ${successCount} user(s)`
          : `Completed: ${successCount} succeeded, ${failCount} failed`,
    });
  }, [userRows]);

  const pendingCount = userRows.filter(
    u =>
      u.normalizeStatus === "pending" &&
      u.firms.length === 1 &&
      isValidFirmRef(u.firms[0]?.firmRef),
  ).length;

  const columns: Column<UserRow>[] = useMemo(
    () => [
      {
        header: "Email",
        accessor: "email" as keyof UserRow,
        render: (value: unknown) => String(value),
      },
      {
        header: "External ID",
        accessor: "externalId" as keyof UserRow,
        render: (value: unknown) => String(value),
      },
      {
        header: "Firms",
        accessor: "firms" as keyof UserRow,
        render: (_value: unknown, row: UserRow) => {
          if (row.firms.length === 0) return "—";
          return (
            <div>
              {row.firms.map(f => (
                <div key={f.firmRef}>
                  {f.firmRef} — {f.firmName}
                </div>
              ))}
              {row.firms.length > 1 && (
                <div className={styles.resultDetails}>{row.firms.length} firms</div>
              )}
            </div>
          );
        },
      },
      {
        header: "Unverified Files",
        accessor: "unverifiedFileCount" as keyof UserRow,
        render: (value: unknown) => String(value),
      },
      {
        header: "Unverified Submissions",
        accessor: "unverifiedSubmissionCount" as keyof UserRow,
        render: (value: unknown) => String(value),
      },
      {
        header: "Submissions without Firm Ref",
        accessor: "submissionsWithoutFirmRefCount" as keyof UserRow,
        render: (value: unknown) => String(value),
      },
      {
        header: "Status",
        accessor: "normalizeStatus" as keyof UserRow,
        render: (value: unknown, row: UserRow) => {
          const status = value as NormalizeStatus;
          return (
            <div>
              <StatusBadge status={status} />
              {status === "success" && row.result && (
                <div className={styles.resultDetails}>
                  {row.result.updatedSubmissions} submissions, {row.result.updatedDocumentFiles}{" "}
                  docs, {row.result.updatedRefereeFiles} referees
                  {(row.result.skippedSubmissions ?? 0) > 0 &&
                    `, ${row.result.skippedSubmissions} skipped (duplicate)`}
                </div>
              )}
              {status === "failed" && row.error && (
                <div className={styles.resultDetails}>{row.error}</div>
              )}
            </div>
          );
        },
      },
      {
        header: "Actions",
        accessor: "externalId" as keyof UserRow,
        render: (_value: unknown, row: UserRow) => {
          if (row.normalizeStatus === "loading") {
            return (
              <Button variant="outlined" color="primary" size="small" disabled>
                Normalizing...
              </Button>
            );
          }

          if (row.normalizeStatus === "success") {
            return null;
          }

          const hasAnyValidFirm = row.firms.some(f => isValidFirmRef(f.firmRef));
          return (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={normalizeAllLoading || !hasAnyValidFirm}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                handleNormalize(row);
              }}
            >
              Normalize
            </Button>
          );
        },
      },
    ],
    [handleNormalize, normalizeAllLoading],
  );

  const status = (error as Error & { status?: number })?.status;
  const isAuthError = status === 401 || status === 403;

  if (isAuthError) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.content}>
      {notification && (
        <div className={`${styles.notification} ${styles[notification.type]}`}>
          {notification.message}
          <button
            type="button"
            onClick={() => setNotification(null)}
            className={styles.notificationClose}
            aria-label="Dismiss notification"
          >
            x
          </button>
        </div>
      )}

      {loading && <LoadingState message="Loading users with unverified files..." />}

      {error && !isAuthError && (
        <EmptyState
          variant="error"
          title="Failed to load users"
          message={error.message}
          action={{
            label: "Retry",
            onClick: refetch,
          }}
        />
      )}

      {!loading && !error && userRows.length === 0 && (
        <EmptyState
          variant="empty"
          title="No users with unverified files"
          message="There are no users with unverified files."
        />
      )}

      {!loading && !error && userRows.length > 0 && (
        <>
          <div className={styles.toolbar}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNormalizeAll}
              disabled={normalizeAllLoading || pendingCount === 0}
            >
              {normalizeAllLoading ? "Normalizing..." : `Normalize All (${pendingCount})`}
            </Button>
          </div>

          <div className={styles.tableContainer}>
            <DataTable data={userRows} columns={columns} rowKey="externalId" size="small" />
          </div>
        </>
      )}
    </div>
  );
}

export default UnverifiedFilesTab;
